// Wave 3.45 Phase 4 — Strict Reconciliation Worksheet
// =============================================================================
// RepairReceiveDialog
// =============================================================================
// "Receive Repaired" dialog. Combines:
//   * Live reconciliation math (incoming + issued − returned vs outgoing).
//   * Inline "+ Issue more material" panel for resolving POSITIVE gaps.
//   * Per-line excess routing (Unattributed Pool | Broken Packet) for
//     resolving NEGATIVE gaps. Broken Packet selector includes an inline
//     "+ Create new" option that opens the Phase 2 BrokenPacketCreateDialog.
//   * Confirm button stays DISABLED unless ``gap === 0`` (zero-tolerance
//     enforcement — strict math, per user decision Q1=b).
//
// On submit, posts to POST /api/repairs/items/{item_id}/receive.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  CheckCircle2,
  AlertTriangle,
  Plus,
  Trash2,
  Loader2,
  Wrench,
  PackageX,
  ArrowUpRight,
  ArrowDownLeft,
  Scale,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { BrokenPacketCreateDialog } from '@/components/broken-packets/BrokenPacketCreateDialog';

const CT_TO_G = 0.2;

const MATERIAL_LABELS = {
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
};

const MATERIAL_API = {
  diamond: '/diamonds',
  color_stone: '/color-stones',
  pearl: '/pearls',
};

export function RepairReceiveDialog({ open, onOpenChange, item, companyId, onCompleted }) {
  // ---------------------------------------------------------- live state
  const [snapshot, setSnapshot] = useState(null);
  const [outgoing, setOutgoing] = useState('');
  const [notes, setNotes] = useState('');
  const [extraGold, setExtraGold] = useState([]);          // [{purity_id, grams}]
  const [extraCarat, setExtraCarat] = useState([]);        // [{material, packet_id, packet_no, carats, pieces}]
  const [excessGold, setExcessGold] = useState([]);        // [{purity_id, grams, destination, note}]
  const [excessCarat, setExcessCarat] = useState([]);      // [{material, classification_id, carats, pieces, destination, broken_packet_id, note}]
  // Master data
  const [purities, setPurities] = useState([]);
  const [diamondClass, setDiamondClass] = useState([]);
  const [csClass, setCsClass] = useState([]);
  const [pearlClass, setPearlClass] = useState([]);
  const [normalPackets, setNormalPackets] = useState({ diamond: [], color_stone: [], pearl: [] });
  const [brokenPackets, setBrokenPackets] = useState({ diamond: [], color_stone: [], pearl: [] });
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [brokenDialogOpen, setBrokenDialogOpen] = useState(false);
  const [brokenDialogMaterial, setBrokenDialogMaterial] = useState('diamond');

  useEffect(() => {
    if (!open || !item?.id) return;
    setOutgoing('');
    setNotes('');
    setExtraGold([]);
    setExtraCarat([]);
    setExcessGold([]);
    setExcessCarat([]);
    loadSnapshot();
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const loadSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const { data } = await api.get(`/repairs/items/${item.id}/reconciliation-snapshot`);
      setSnapshot(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load reconciliation snapshot'));
    } finally {
      setSnapshotLoading(false);
    }
  };

  const loadMasters = async () => {
    try {
      const [pur, dia, cs, pl] = await Promise.all([
        api.get('/gold/purities', { params: { company_id: companyId } }),
        api.get('/diamonds/classifications', { params: { company_id: companyId } }),
        api.get('/color-stones/classifications', { params: { company_id: companyId } }),
        api.get('/pearls/classifications', { params: { company_id: companyId } }),
      ]);
      setPurities(pur.data || []);
      setDiamondClass(dia.data || []);
      setCsClass(cs.data || []);
      setPearlClass(pl.data || []);
      // packets — for normal we only need non-empty; for broken everything.
      const [dp, cp, pp, dbp, cbp, pbp] = await Promise.all([
        api.get('/diamonds/packets', { params: { company_id: companyId } }),
        api.get('/color-stones/packets', { params: { company_id: companyId } }),
        api.get('/pearls/packets', { params: { company_id: companyId } }),
        api.get('/diamonds/packets', { params: { company_id: companyId, packet_type: 'broken' } }),
        api.get('/color-stones/packets', { params: { company_id: companyId, packet_type: 'broken' } }),
        api.get('/pearls/packets', { params: { company_id: companyId, packet_type: 'broken' } }),
      ]);
      setNormalPackets({
        diamond: (dp.data || []).filter((p) => Number(p.balance_carats || 0) > 0),
        color_stone: (cp.data || []).filter((p) => Number(p.balance_carats || 0) > 0),
        pearl: (pp.data || []).filter((p) => Number(p.balance_carats || 0) > 0),
      });
      setBrokenPackets({
        diamond: dbp.data || [],
        color_stone: cbp.data || [],
        pearl: pbp.data || [],
      });
    } catch (e) {
      toast.error(extractError(e, 'Failed to load master data'));
    }
  };

  // -------------------------------------------------------- math (live)
  const reconciliation = useMemo(() => {
    if (!snapshot) return null;
    const incoming = Number(snapshot.incoming_gross_g || 0);
    const base = snapshot.movements?.totals || {};
    const baseIssued = Number(base.issued_g || 0);
    const baseReturned = Number(base.returned_g || 0);
    const extraGoldG = extraGold.reduce((a, x) => a + Number(x.grams || 0), 0);
    const extraStoneG = extraCarat.reduce((a, x) => a + Number(x.carats || 0) * CT_TO_G, 0);
    const excessGoldG = excessGold.reduce((a, x) => a + Number(x.grams || 0), 0);
    const excessStoneG = excessCarat.reduce((a, x) => a + Number(x.carats || 0) * CT_TO_G, 0);
    const payloadAdded = extraGoldG + extraStoneG;
    const payloadRemoved = excessGoldG + excessStoneG;
    const expectedOutgoing = +(
      incoming + (baseIssued + payloadAdded) - (baseReturned + payloadRemoved)
    ).toFixed(3);
    const outNum = Number(outgoing || 0);
    const gap = +(outNum - expectedOutgoing).toFixed(3);
    return {
      incoming,
      base_issued: baseIssued,
      base_returned: baseReturned,
      payload_added: payloadAdded,
      payload_removed: payloadRemoved,
      expected_outgoing: expectedOutgoing,
      outgoing: outNum,
      gap,
      balanced: outNum > 0 && Math.abs(gap) < 1e-4,
    };
  }, [snapshot, outgoing, extraGold, extraCarat, excessGold, excessCarat]);

  const isExternalDetailed = item?.intake_type === 'external' && item?.detail_mode === 'detailed';

  // -------------------------------------------------------- submit
  const submit = async () => {
    if (!reconciliation?.balanced) {
      toast.error('Cannot submit — gap must be exactly 0.000 g');
      return;
    }
    // Validate broken packet picks
    for (const r of excessCarat) {
      if (r.destination === 'broken_packet' && !r.broken_packet_id) {
        toast.error(`Pick a Broken ${MATERIAL_LABELS[r.material]} packet for the excess route`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        outgoing_gross_weight_g: Number(outgoing),
        notes: notes.trim(),
        extra_gold_issues: extraGold
          .filter((x) => x.purity_id && Number(x.grams) > 0)
          .map((x) => ({ purity_id: x.purity_id, grams: Number(x.grams) })),
        extra_carat_issues: extraCarat
          .filter((x) => x.material && x.packet_id && Number(x.carats) > 0)
          .map((x) => ({
            material: x.material,
            packet_id: x.packet_id,
            carats: Number(x.carats),
            pieces: Number(x.pieces || 0),
          })),
        excess_gold_routes: excessGold
          .filter((x) => x.purity_id && Number(x.grams) > 0)
          .map((x) => ({
            purity_id: x.purity_id,
            grams: Number(x.grams),
            destination: x.destination || 'unattributed',
            note: x.note || '',
          })),
        excess_carat_routes: excessCarat
          .filter((x) => x.material && (Number(x.carats) > 0 || Number(x.pieces) > 0))
          .map((x) => ({
            material: x.material,
            classification_id: x.classification_id || null,
            classification_label: x.classification_label || '',
            carats: Number(x.carats || 0),
            pieces: Number(x.pieces || 0),
            destination: x.destination || 'unattributed',
            broken_packet_id: x.destination === 'broken_packet' ? x.broken_packet_id : null,
            note: x.note || '',
          })),
      };
      const { data } = await api.post(`/repairs/items/${item.id}/receive`, payload);
      toast.success(`Reconciliation locked at gap 0.000 g — status: ${data.status}`);
      onCompleted?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(extractError(e, 'Reconciliation failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------- render
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="repair-receive-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Wrench size={18} className="text-[var(--ink-navy)]" />
            <DialogTitle>Receive Repaired Piece</DialogTitle>
          </div>
          <DialogDescription>
            Strict zero-tolerance reconciliation. The Confirm button only
            unlocks when the total weight gap is exactly{' '}
            <span className="font-mono">0.000 g</span>. 1 ct = 0.2 g.
          </DialogDescription>
        </DialogHeader>

        {/* Live math summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-2">
          <MathCell
            icon={<Scale size={14} />}
            label="Incoming gross"
            value={reconciliation ? `${reconciliation.incoming.toFixed(3)} g` : '—'}
            tone="navy"
            testId="math-incoming"
          />
          <MathCell
            icon={<ArrowUpRight size={14} />}
            label="Issued during repair"
            value={reconciliation
              ? `+${(reconciliation.base_issued + reconciliation.payload_added).toFixed(3)} g`
              : '—'}
            tone="emerald"
            testId="math-issued"
          />
          <MathCell
            icon={<ArrowDownLeft size={14} />}
            label="Returned during repair"
            value={reconciliation
              ? `-${(reconciliation.base_returned + reconciliation.payload_removed).toFixed(3)} g`
              : '—'}
            tone="amber"
            testId="math-returned"
          />
          <MathCell
            icon={<Scale size={14} />}
            label="Expected outgoing"
            value={reconciliation ? `${reconciliation.expected_outgoing.toFixed(3)} g` : '—'}
            tone="violet"
            testId="math-expected"
          />
          <GapCell reconciliation={reconciliation} testId="math-gap" />
        </div>

        {/* Outgoing weight input */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-[11px] text-slate-500">
              Outgoing gross weight (g) <span className="text-rose-600">*</span>
            </Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={outgoing}
              onChange={(e) => setOutgoing(e.target.value)}
              placeholder="What the piece weighs now"
              className="h-9 font-mono tabular-nums"
              data-testid="receive-outgoing-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Use a calibrated scale. Math must balance to 0.000 g.
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything noteworthy about the receive…"
              rows={2}
              data-testid="receive-notes-input"
            />
          </div>
        </div>

        {/* Positive gap helper — extra issues */}
        {reconciliation && reconciliation.gap > 0.0005 && (
          <GapHelper
            tone="positive"
            gap={reconciliation.gap}
            title="Positive gap — piece is HEAVIER than expected."
            subtitle="Add inline issues to balance: material was added during repair that hasn't been tracked yet."
          />
        )}

        {/* Inline extra-issue panel (positive gap) */}
        <CollapsiblePanel
          title="Extra issues to post during this receive (positive gap resolution)"
          subtitle="Post additional gold or stones to the goldsmith for this order. Each issue is a real ledger movement."
          dataTestId="extra-issues-panel"
        >
          <ExtraGoldList
            lines={extraGold}
            onChange={setExtraGold}
            purities={purities}
          />
          <ExtraCaratList
            lines={extraCarat}
            onChange={setExtraCarat}
            normalPackets={normalPackets}
          />
        </CollapsiblePanel>

        {/* Negative gap helper */}
        {reconciliation && reconciliation.gap < -0.0005 && (
          <GapHelper
            tone="negative"
            gap={reconciliation.gap}
            title="Negative gap — piece is LIGHTER than expected."
            subtitle="Route the excess gold to Unattributed Pool or Gold Loss (write-off, e.g. polishing). Stones may go to the Unattributed Pool or a Broken Packet. Polish-only repairs typically need no extras and just a gold-loss line."
          />
        )}

        {/* Excess routing panel (negative gap) */}
        <CollapsiblePanel
          title="Excess to route out (negative gap resolution)"
          subtitle="Materials that left the customer's piece. Pick destination per line."
          dataTestId="excess-routes-panel"
        >
          <ExcessGoldList
            lines={excessGold}
            onChange={setExcessGold}
            purities={purities}
          />
          <ExcessCaratList
            lines={excessCarat}
            onChange={setExcessCarat}
            diamondClass={diamondClass}
            csClass={csClass}
            pearlClass={pearlClass}
            brokenPackets={brokenPackets}
            onCreateBroken={(mat) => { setBrokenDialogMaterial(mat); setBrokenDialogOpen(true); }}
          />
        </CollapsiblePanel>

        {/* Escrow hint (External + Detailed only) */}
        {isExternalDetailed && (
          <div className="mt-3 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              Customer escrow is automatically updated for any excess routes
              (these materials are deducted from the customer's escrow ledger).
              The remaining escrow is released when you mark the piece as
              returned to the customer.
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 sticky bottom-0 bg-white pt-3 border-t border-[var(--divider)]">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="receive-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || snapshotLoading || !reconciliation?.balanced}
            className={
              reconciliation?.balanced
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'opacity-70 cursor-not-allowed'
            }
            data-testid="receive-submit-button"
          >
            {submitting ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 size={14} className="mr-1.5" />
            )}
            {submitting ? 'Posting…' : 'Confirm Reconciliation'}
          </Button>
        </DialogFooter>

        <BrokenPacketCreateDialog
          open={brokenDialogOpen}
          onOpenChange={setBrokenDialogOpen}
          material={brokenDialogMaterial}
          companyId={companyId}
          onCreated={() => loadMasters()}
        />
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Subcomponents
// =============================================================================

function MathCell({ icon, label, value, tone, testId }) {
  const toneCls = {
    navy: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  }[tone] || '';
  return (
    <div
      className={`border rounded-md px-3 py-2 ${toneCls}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-75">
        {icon}
        {label}
      </div>
      <div className="font-mono tabular-nums text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}

function GapCell({ reconciliation, testId }) {
  const cls = reconciliation?.balanced
    ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
    : Math.abs(reconciliation?.gap || 0) > 0.0005
      ? 'bg-rose-50 border-rose-300 text-rose-900 animate-pulse'
      : 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border rounded-md px-3 py-2 ${cls}`} data-testid={testId}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-75">
        {reconciliation?.balanced ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        Variance gap
      </div>
      <div className="font-mono tabular-nums text-sm font-semibold mt-0.5">
        {reconciliation
          ? `${reconciliation.gap >= 0 ? '+' : ''}${reconciliation.gap.toFixed(3)} g`
          : '—'}
      </div>
    </div>
  );
}

function GapHelper({ tone, gap, title, subtitle }) {
  const cls = tone === 'positive'
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  return (
    <div className={`mt-3 border rounded-md px-3 py-2 text-[12px] ${cls}`}>
      <div className="font-medium flex items-center gap-1">
        <AlertTriangle size={14} /> {title} ({gap > 0 ? '+' : ''}{gap.toFixed(3)} g)
      </div>
      <div className="text-[11px] opacity-80 mt-0.5">{subtitle}</div>
    </div>
  );
}

function CollapsiblePanel({ title, subtitle, children, dataTestId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border border-[var(--divider)] rounded-md" data-testid={dataTestId}>
      <button
        type="button"
        className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-slate-50"
        onClick={() => setOpen(!open)}
      >
        <div>
          <div className="text-sm font-medium text-[var(--ink-navy)]">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
        <span className="text-[12px] text-slate-500">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-3">{children}</div>}
    </div>
  );
}

function ExtraGoldList({ lines, onChange, purities }) {
  const add = () => onChange((l) => [...l, { purity_id: '', grams: '' }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[11px] text-slate-500">Extra GOLD issues</Label>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={add} data-testid="add-extra-gold-line">
          <Plus size={12} className="mr-1" /> Add line
        </Button>
      </div>
      {lines.length === 0 ? (
        <div className="text-[10px] text-slate-400 italic">No extra gold issues yet.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((gl, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`extra-gold-row-${i}`}>
              <div className="col-span-7">
                <Label className="text-[10px] text-slate-500">Purity</Label>
                <SearchableSelect
                  items={purities || []}
                  value={gl.purity_id}
                  onValueChange={(v) => update(i, { purity_id: v || '' })}
                  itemToLabel={(p) => `${p.name} (${p.percent}%)`}
                  searchKeys={['name']}
                  placeholder="Select purity"
                  searchPlaceholder="Search purity…"
                />
              </div>
              <div className="col-span-4">
                <Label className="text-[10px] text-slate-500">Grams</Label>
                <Input
                  type="number" step="0.001" min="0"
                  value={gl.grams}
                  onChange={(e) => update(i, { grams: e.target.value })}
                  className="h-9 font-mono tabular-nums"
                  data-testid={`extra-gold-grams-${i}`}
                />
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="sm" className="h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={() => remove(i)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExtraCaratList({ lines, onChange, normalPackets }) {
  const add = (material) => onChange((l) => [...l, { material, packet_id: '', carats: '', pieces: '' }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[11px] text-slate-500">Extra STONE issues</Label>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('diamond')}>
            + Diamond
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('color_stone')}>
            + Colour Stone
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('pearl')}>
            + Pearl
          </Button>
        </div>
      </div>
      {lines.length === 0 ? (
        <div className="text-[10px] text-slate-400 italic">No extra stone issues yet.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((cl, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`extra-carat-row-${i}`}>
              <div className="col-span-1">
                <Badge variant="secondary" className="text-[9px]">{MATERIAL_LABELS[cl.material]}</Badge>
              </div>
              <div className="col-span-5">
                <Label className="text-[10px] text-slate-500">Source packet (purchased)</Label>
                <SearchableSelect
                  items={normalPackets[cl.material] || []}
                  value={cl.packet_id}
                  onValueChange={(v) => update(i, { packet_id: v || '' })}
                  itemToLabel={(p) => `${p.packet_no} · ${Number(p.balance_carats).toFixed(2)} ct / ${p.balance_pieces} pcs`}
                  itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
                  searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
                  placeholder="Pick a packet"
                  searchPlaceholder="Search packet no…"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Carats</Label>
                <Input type="number" step="0.01" min="0" value={cl.carats} onChange={(e) => update(i, { carats: e.target.value })} className="h-9 font-mono tabular-nums" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Pieces</Label>
                <Input type="number" step="1" min="0" value={cl.pieces} onChange={(e) => update(i, { pieces: e.target.value })} className="h-9 font-mono tabular-nums" />
              </div>
              <div className="col-span-2 text-right">
                <Button variant="ghost" size="sm" className="h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={() => remove(i)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExcessGoldList({ lines, onChange, purities }) {
  const add = () => onChange((l) => [...l, { purity_id: '', grams: '', destination: 'unattributed', note: '' }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[11px] text-slate-500">
          Excess GOLD routes
          <span className="text-[10px] text-slate-400 font-normal ml-1">
            (Unattributed Pool or Gold Loss write-off)
          </span>
        </Label>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={add} data-testid="add-excess-gold-line">
          <Plus size={12} className="mr-1" /> Add line
        </Button>
      </div>
      {lines.length === 0 ? (
        <div className="text-[10px] text-slate-400 italic">No excess gold routes yet.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((gl, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`excess-gold-row-${i}`}>
              <div className="col-span-4">
                <Label className="text-[10px] text-slate-500">Purity</Label>
                <SearchableSelect
                  items={purities || []}
                  value={gl.purity_id}
                  onValueChange={(v) => update(i, { purity_id: v || '' })}
                  itemToLabel={(p) => `${p.name} (${p.percent}%)`}
                  searchKeys={['name']}
                  placeholder="Select purity"
                  searchPlaceholder="Search purity…"
                  testId={`excess-gold-purity-${i}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Grams</Label>
                <Input type="number" step="0.001" min="0" value={gl.grams} onChange={(e) => update(i, { grams: e.target.value })} className="h-9 font-mono tabular-nums" data-testid={`excess-gold-grams-${i}`} />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] text-slate-500">Destination</Label>
                <Select value={gl.destination || 'unattributed'} onValueChange={(v) => update(i, { destination: v })}>
                  <SelectTrigger className="h-9" data-testid={`excess-gold-destination-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unattributed">Unattributed Pool</SelectItem>
                    <SelectItem value="gold_loss">Gold Loss (write-off)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 text-[10px] text-slate-500 self-center leading-tight">
                {gl.destination === 'gold_loss'
                  ? 'Polishing/dust loss. Not credited anywhere.'
                  : 'Goldsmith retains in unattributed pool.'}
              </div>
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="sm" className="h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={() => remove(i)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExcessCaratList({ lines, onChange, diamondClass, csClass, pearlClass, brokenPackets, onCreateBroken }) {
  const add = (material) =>
    onChange((l) => [...l, {
      material,
      classification_id: '', classification_label: '',
      carats: '', pieces: '',
      destination: 'unattributed', broken_packet_id: '',
      note: '',
    }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const classes = (material) =>
    material === 'diamond' ? diamondClass : material === 'color_stone' ? csClass : pearlClass;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[11px] text-slate-500">Excess STONE routes</Label>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('diamond')}>
            + Diamond
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('color_stone')}>
            + Colour Stone
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => add('pearl')}>
            + Pearl
          </Button>
        </div>
      </div>
      {lines.length === 0 ? (
        <div className="text-[10px] text-slate-400 italic">No excess stone routes yet.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((cl, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`excess-carat-row-${i}`}>
              <div className="col-span-1">
                <Badge variant="secondary" className="text-[9px]">{MATERIAL_LABELS[cl.material]}</Badge>
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] text-slate-500">Classification</Label>
                <SearchableSelect
                  items={classes(cl.material) || []}
                  value={cl.classification_id || ''}
                  onValueChange={(v) => update(i, { classification_id: v || '' })}
                  itemToLabel={(c) => c.schema_code}
                  itemToSubLabel={(c) => c.shape_name || null}
                  searchKeys={['schema_code', 'shape_name']}
                  placeholder="Pick classification"
                  searchPlaceholder="Search…"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-[10px] text-slate-500">Carats</Label>
                <Input type="number" step="0.01" min="0" value={cl.carats} onChange={(e) => update(i, { carats: e.target.value })} className="h-9 font-mono tabular-nums" />
              </div>
              <div className="col-span-1">
                <Label className="text-[10px] text-slate-500">Pieces</Label>
                <Input type="number" step="1" min="0" value={cl.pieces} onChange={(e) => update(i, { pieces: e.target.value })} className="h-9 font-mono tabular-nums" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Destination</Label>
                <Select value={cl.destination} onValueChange={(v) => update(i, { destination: v })}>
                  <SelectTrigger className="h-9" data-testid={`excess-carat-destination-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unattributed">Unattributed Pool</SelectItem>
                    <SelectItem value="broken_packet">Broken Packet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cl.destination === 'broken_packet' ? (
                <div className="col-span-3">
                  <Label className="text-[10px] text-slate-500 flex items-center gap-1">
                    Broken packet
                    <button
                      type="button"
                      className="ml-auto text-[10px] text-rose-700 hover:underline inline-flex items-center"
                      onClick={() => onCreateBroken(cl.material)}
                    >
                      <PackageX size={10} className="mr-0.5" /> + Create new
                    </button>
                  </Label>
                  <SearchableSelect
                    items={brokenPackets[cl.material] || []}
                    value={cl.broken_packet_id || ''}
                    onValueChange={(v) => update(i, { broken_packet_id: v || '' })}
                    itemToLabel={(p) => p.packet_no}
                    itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
                    searchKeys={['packet_no', 'classification_shape', 'classification_schema']}
                    placeholder={(brokenPackets[cl.material] || []).length === 0 ? 'None yet — click "+ Create new" above' : 'Pick broken packet'}
                    searchPlaceholder="Search broken packet…"
                    disabled={(brokenPackets[cl.material] || []).length === 0}
                    testId={`excess-carat-broken-pkt-${i}`}
                  />
                </div>
              ) : (
                <div className="col-span-3 text-[10px] text-slate-500 self-center">
                  Goldsmith retains in their unattributed pool.
                </div>
              )}
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="sm" className="h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={() => remove(i)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RepairReceiveDialog;
